// The one dialog that turns anything into a link.
//
// Used for past workouts, the plan currently in the Session tab, session
// recipes and programmes. What differs between them is only the `request`
// object handed in; the flow, the wording of the privacy choice and the
// copy/share affordances are identical everywhere, which is the point of
// having a single component.
//
// The link is not created until the user asks for it. Opening the dialog to
// look at the options must not leave a live, shareable snapshot of a workout
// sitting on the server.

import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertCircle,
  Check,
  Copy,
  Link2,
  Loader2,
  Share2,
  UserPlus,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import LoginButton from "../auth/LoginButton";
import { createShare, createInvite } from "../../services/api";
import { buildShareUrl, shareOrCopyLink } from "../../utils/shareLinks";

const EXPIRY_OPTIONS = [
  { value: "never", label: "Never expires" },
  { value: "1", label: "Expires in 1 day" },
  { value: "7", label: "Expires in 7 days" },
  { value: "30", label: "Expires in 30 days" },
];

const KIND_NOUNS = {
  session: "workout",
  plan: "workout",
  recipe: "recipe",
  programme: "programme",
};

const ShareLinkDialog = ({ open, onOpenChange, request, defaultTitle }) => {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const [title, setTitle] = useState(defaultTitle || "");
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState("link");
  const [expiry, setExpiry] = useState("never");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [inviting, setInviting] = useState(false);

  const noun = KIND_NOUNS[request?.kind] || "workout";

  // Reset on each open so a dialog reopened after a previous share does not
  // show the old link as though it were the new one.
  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle || "");
    setNote("");
    setVisibility("link");
    setExpiry("never");
    setShare(null);
    setError(null);
    setCopied(false);
  }, [open, defaultTitle]);

  const shareUrl = share ? buildShareUrl(share.share_code) : "";

  const handleCreate = useCallback(async () => {
    if (!request) return;
    setCreating(true);
    setError(null);
    try {
      const created = await createShare({
        ...request,
        title: title.trim() || request.title || defaultTitle || null,
        note: note.trim() || null,
        visibility,
        expires_in_days: expiry === "never" ? null : Number(expiry),
      });
      setShare(created);

      // Offer the link straight away on platforms with a share sheet — the
      // common case is "make it and send it", not "make it and admire it".
      const url = buildShareUrl(created.share_code);
      const outcome = await shareOrCopyLink({
        url,
        title: title.trim() || defaultTitle || "Khronos session",
        text: `Here's a ${noun} from Khronos`,
      });
      if (outcome === "copied") setCopied(true);
    } catch (err) {
      setError(err.message || "Could not create the link.");
    } finally {
      setCreating(false);
    }
  }, [request, title, note, visibility, expiry, defaultTitle, noun]);

  const handleCopy = useCallback(async () => {
    const outcome = await shareOrCopyLink({
      url: shareUrl,
      title: title.trim() || defaultTitle || "Khronos session",
      text: `Here's a ${noun} from Khronos`,
    });
    if (outcome === "copied") {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (outcome === "failed") {
      setError("Could not copy the link. Select and copy it manually.");
    }
  }, [shareUrl, title, defaultTitle, noun]);

  // Upgrades the plain share into an invitation: same workout, but whoever
  // opens it also ends up connected to the sharer.
  const handleInviteWithLink = useCallback(async () => {
    if (!share) return;
    setInviting(true);
    setError(null);
    try {
      const invite = await createInvite({
        kind: "session",
        share_code: share.share_code,
        note: note.trim() || null,
      });
      const url = `${window.location.origin}/?i=${encodeURIComponent(invite.code)}`;
      const outcome = await shareOrCopyLink({
        url,
        title: "Train with me on Khronos",
        text: `Join me on Khronos — here's a ${noun} to start with`,
      });
      showToast({
        title: outcome === "shared" ? "Invitation sent" : "Invitation link copied",
        description:
          "Whoever opens it gets the session and is added to your mates.",
      });
    } catch (err) {
      setError(err.message || "Could not create the invitation.");
    } finally {
      setInviting(false);
    }
  }, [share, note, noun, showToast]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share this {noun}
          </DialogTitle>
          <DialogDescription>
            {share
              ? "Anyone with this link can open it. They'll need to sign in to load it."
              : `Creates a link to a copy of this ${noun}. Your later edits won't change it.`}
          </DialogDescription>
        </DialogHeader>

        {!isAuthenticated ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Sharing needs an account, so people know who sent it.
            </p>
            <LoginButton className="w-full" />
          </div>
        ) : share ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="min-w-0 font-mono text-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                title="Copy link"
                aria-label="Copy link"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleInviteWithLink}
              disabled={inviting}
            >
              {inviting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Send as an invitation instead
            </Button>
            <p className="-mt-2 text-xs text-muted-foreground">
              Same {noun}, but whoever opens it also becomes one of your mates.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="share-title">Name</Label>
              <Input
                id="share-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={defaultTitle || "Shared session"}
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="share-note">Message (optional)</Label>
              <Input
                id="share-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Give this a go — it's a tough one"
                maxLength={280}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Who can open it</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="link">Anyone with the link</SelectItem>
                    <SelectItem value="friends">My mates only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Expiry</Label>
                <Select value={expiry} onValueChange={setExpiry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Only the workout itself is shared — never your times, heart rate,
              notes or rating.
            </p>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {share ? "Done" : "Cancel"}
          </Button>
          {isAuthenticated && !share && (
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Create link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareLinkDialog;
